import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { buildGrowthMarketingChannelOptions, growthPublishChannelLabel } from './growth-channel.constants';
import { GrowthOptimizationGuardrailPolicy, GrowthOptimizationService, safeJsonArray } from './growth-optimization.service';

@Component({
  standalone: true,
  selector: 'ain-growth-optimizer-guardrails',
  imports: [
    FormsModule,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    ButtonModule,
    TableModule,
    InputTextModule,
    InputNumberModule,
    MultiSelectModule,
    CheckboxModule,
    DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.optimization.guardrails.title')" [description]="t('growth.optimization.guardrails.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.optimization.guardrails.help.title'"
        [descriptionKey]="'growth.optimization.guardrails.help.description'"
        [bulletKeys]="['growth.optimization.guardrails.help.bullets.autoInLimit', 'growth.optimization.guardrails.help.bullets.simulation']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('common.add')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('growth.optimization.guardrails.empty')" />
          } @else {
            <p-table [value]="rows()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.optimization.guardrails.columns.policyKey') }}</th>
                  <th>{{ t('growth.optimization.guardrails.columns.optimizerTypes') }}</th>
                  <th>{{ t('growth.optimization.guardrails.columns.allowedChannels') }}</th>
                  <th>{{ t('growth.optimization.guardrails.columns.status') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.policyKey }}</td>
                  <td>{{ toCsv(row.appliesToOptimizerTypesJson) }}</td>
                  <td>{{ channelLabelsCsv(row.allowedChannelsJson) }}</td>
                  <td>{{ row.isActive ? t('common.active') : t('common.disabled') }}</td>
                  <td><button pButton type="button" size="small" [label]="t('common.edit')" (click)="edit(row)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog
        [header]="t('growth.optimization.guardrails.title')"
        [visible]="dialogVisible()"
        (visibleChange)="dialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(860px, 96vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.policyKey')">
            <input pInputText [(ngModel)]="form.policyKey" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.maxBudgetChangePercent')">
            <p-inputNumber [(ngModel)]="form.maxBudgetChangePercent" [min]="0" [maxFractionDigits]="2" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.maxSpendAmount')">
            <p-inputNumber [(ngModel)]="form.maxSpendAmount" [min]="0" [maxFractionDigits]="2" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.maxAudienceBlastRadius')">
            <p-inputNumber [(ngModel)]="form.maxAudienceBlastRadius" [min]="0" [maxFractionDigits]="2" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.optimizerTypes')">
            <p-multiselect [options]="optimizerTypeOptions" [(ngModel)]="form.appliesToOptimizerTypes" optionLabel="label" optionValue="value" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.optimization.guardrails.fields.allowedChannels')">
            <p-multiselect [options]="channelOptions()" [(ngModel)]="form.allowedChannels" optionLabel="label" optionValue="value" appendTo="body" />
          </ain-form-field>
        </div>
        <div class="toggles">
          <label><p-checkbox [(ngModel)]="form.requireSimulation" [binary]="true" /> {{ t('growth.optimization.guardrails.fields.requireSimulation') }}</label>
          <label><p-checkbox [(ngModel)]="form.requireWorkflowApproval" [binary]="true" /> {{ t('growth.optimization.guardrails.fields.requireWorkflowApproval') }}</label>
          <label><p-checkbox [(ngModel)]="form.autoInLimitEnabled" [binary]="true" /> {{ t('growth.optimization.guardrails.fields.autoInLimitEnabled') }}</label>
          <label><p-checkbox [(ngModel)]="form.isActive" [binary]="true" /> {{ t('common.active') }}</label>
        </div>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .toggles { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-top: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthOptimizerGuardrailsComponent {
  private readonly service = inject(GrowthOptimizationService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly dialogVisible = signal(false);
  readonly rows = signal<GrowthOptimizationGuardrailPolicy[]>([]);

  form = {
    policyKey: 'default-growth-guardrail',
    appliesToOptimizerTypes: ['Content', 'Budget'],
    maxBudgetChangePercent: 10,
    maxSpendAmount: 1000,
    maxAudienceBlastRadius: 30,
    allowedChannels: ['web', 'social'],
    requireSimulation: true,
    requireWorkflowApproval: true,
    autoInLimitEnabled: false,
    isActive: true
  };

  readonly optimizerTypeOptions = [
    { label: 'Content', value: 'Content' },
    { label: 'Budget', value: 'Budget' },
    { label: 'Campaign', value: 'Campaign' },
    { label: 'SeoGeo', value: 'SeoGeo' },
    { label: 'Conversion', value: 'Conversion' },
    { label: 'Revenue', value: 'Revenue' }
  ];

  constructor() {
    this.load();
  }

  t(key: string): string { return this.i18n.translate(key); }

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  toCsv(json: string): string {
    return safeJsonArray(json).map(item => `${item}`).join(', ');
  }

  channelLabelsCsv(json: string): string {
    return safeJsonArray(json).map(item => growthPublishChannelLabel(`${item}`, key => this.t(key))).join(', ');
  }

  load(): void {
    this.loading.set(true);
    this.service.listGuardrails().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  openDialog(): void {
    this.form = {
      policyKey: 'default-growth-guardrail',
      appliesToOptimizerTypes: ['Content', 'Budget'],
      maxBudgetChangePercent: 10,
      maxSpendAmount: 1000,
      maxAudienceBlastRadius: 30,
      allowedChannels: ['web', 'social'],
      requireSimulation: true,
      requireWorkflowApproval: true,
      autoInLimitEnabled: false,
      isActive: true
    };
    this.dialogVisible.set(true);
  }

  edit(row: GrowthOptimizationGuardrailPolicy): void {
    this.form.policyKey = row.policyKey;
    this.form.appliesToOptimizerTypes = safeJsonArray(row.appliesToOptimizerTypesJson).map(item => `${item}`);
    this.form.maxBudgetChangePercent = row.maxBudgetChangePercent ?? 0;
    this.form.maxSpendAmount = row.maxSpendAmount ?? 0;
    this.form.maxAudienceBlastRadius = row.maxAudienceBlastRadius ?? 0;
    this.form.allowedChannels = safeJsonArray(row.allowedChannelsJson).map(item => `${item}`);
    this.form.requireSimulation = row.requireSimulation;
    this.form.requireWorkflowApproval = row.requireWorkflowApproval;
    this.form.autoInLimitEnabled = row.autoInLimitEnabled;
    this.form.isActive = row.isActive;
    this.dialogVisible.set(true);
  }

  save(): void {
    this.service.upsertGuardrail({
      policyKey: this.form.policyKey,
      appliesToOptimizerTypesJson: JSON.stringify(this.form.appliesToOptimizerTypes),
      maxBudgetChangePercent: this.form.maxBudgetChangePercent,
      maxSpendAmount: this.form.maxSpendAmount,
      maxAudienceBlastRadius: this.form.maxAudienceBlastRadius,
      allowedChannelsJson: JSON.stringify(this.form.allowedChannels),
      requireSimulation: this.form.requireSimulation,
      requireWorkflowApproval: this.form.requireWorkflowApproval,
      autoInLimitEnabled: this.form.autoInLimitEnabled,
      isActive: this.form.isActive
    }).subscribe({ next: () => { this.dialogVisible.set(false); this.load(); } });
  }
}
